import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { File, FileClock } from 'lucide-react';

import { DurationEstimate, RunStatus } from '../../../../../../../backend/src/types/types';
import formatDate from '@/helpers/formatDate';

interface PipelineMessageProps {
  isFailed: boolean;
  status: RunStatus;
  pageCount?: number;
  completedAtEstimate?: Date | null;
  durationEstimate?: DurationEstimate | null;
}

const PipelineMessage = ({
  status,
  isFailed,
  pageCount,
  completedAtEstimate,
  durationEstimate,
}: PipelineMessageProps) => {
  let message = '';
  let subMessage = '';
  if (isFailed) {
    message = 'There was an error processing your case.';
    subMessage =
      'A member of the Wexler technical staff has been informed and will reach out shortly when the issue has been resolved.';
  } else {
    if (status === RunStatus.AwaitingApproval) {
      message = 'Please continue or cancel your current upload.';
    } else if (status === RunStatus.InQueue) {
      message = 'Your documents are about to begin processing.';
    } else {
      message = 'Your documents are currently being processed.';
    }
    subMessage =
      'You may close this page if you wish. You will receive an email when your documents have finished processing.';
  }

  return (
    <div className="flex flex-col text-gray-600">
      <div className="flex items-center justify-between gap-32">
        <div className="flex flex-col gap-1">
          {isFailed ? (
            <div className="flex items-center gap-2 text-lg font-medium">
              <FontAwesomeIcon icon={faExclamationTriangle} className="h-6 w-6 text-red-500" />
              <p>{message}</p>
            </div>
          ) : (
            <p>{message}</p>
          )}
          <p className="text-sm text-gray-400">{subMessage}</p>
        </div>

        {!isFailed && (
          <div className="flex gap-4 text-sm">
            <div className="animate-fade-in flex items-center gap-3 rounded-md border border-dashed border-blue-200 bg-blue-50 px-4 py-2 shadow-sm delay-300">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-100">
                <File className="h-4 w-4 text-blue-600" />
              </div>
              {pageCount ? (
                <div>
                  <div className="text-sm font-semibold text-gray-900">{pageCount.toLocaleString()}</div>
                  <div className="text-xs text-gray-500">Total Credits</div>
                </div>
              ) : (
                <div>
                  <div className="text-sm font-semibold text-gray-900">Calculating...</div>
                  <div className="text-xs text-gray-500">Total Credits</div>
                </div>
              )}
            </div>

            <div className="animate-fade-in flex items-center gap-3 rounded-md border border-dashed border-purple-200 bg-purple-50 px-4 py-2 shadow-sm delay-300">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-purple-100">
                <FileClock className="h-4 w-4 text-purple-600" />
              </div>
              {status === RunStatus.AwaitingApproval ? (
                <div>
                  <div className="text-sm font-semibold text-gray-900">
                    {[
                      (durationEstimate?.days ?? 0) > 0 &&
                        `${durationEstimate?.days} day${durationEstimate?.days !== 1 ? 's' : ''}`,
                      (durationEstimate?.hours ?? 0) > 0 &&
                        `${durationEstimate?.hours} hour${durationEstimate?.hours !== 1 ? 's' : ''}`,
                      (durationEstimate?.minutes ?? 0) > 0 &&
                        `${durationEstimate?.minutes} minute${durationEstimate?.minutes !== 1 ? 's' : ''}`,
                    ]
                      .filter(Boolean)
                      .join(' ')}
                  </div>
                  <div className="text-xs text-gray-500">Estimated Processing Time</div>
                </div>
              ) : completedAtEstimate ? (
                <div>
                  <div className="text-sm font-semibold text-gray-900">
                    {formatDate(completedAtEstimate, true, true)}
                  </div>
                  <div className="text-xs text-gray-500">Estimated Completion Time</div>
                </div>
              ) : (
                <div>
                  <div className="text-sm font-semibold text-gray-900">Calculating...</div>
                  <div className="text-xs text-gray-500">Time Estimate</div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PipelineMessage;
